import { css } from "@emotion/react";
import React from "react";
import { Color, useTheme } from "../theme";
import { Icon } from "../Icon";
import { useClickAnimation } from "../hooks/useClickAnimation";

interface PartialColorProps {
  color: Exclude<Color, "default">;
}

export interface TableCheckboxProps
  extends
    Omit<React.InputHTMLAttributes<HTMLInputElement>, "color">,
    Partial<PartialColorProps> {
  children?: React.ReactNode;
}

export const TableCheckbox = ({
  children,
  onClick: defaultOnClick,
  className,
  color = "primary" as const,
  ...props
}: TableCheckboxProps) => {
  const hasChildren = children !== null && children !== undefined;
  const RootComponent = hasChildren ? "label" : "span";
  const theme = useTheme();
  const { onClick, onAnimationEnd } = useClickAnimation(defaultOnClick);
  return (
    <RootComponent
      className={className}
      css={css({
        position: "relative",
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
      })}
    >
      <input
        css={css`
          position: absolute;
          top: 0;
          left: 0;
          z-index: 1;
          width: 24px;
          height: 24px;
          opacity: 0;
          & + * {
            box-shadow: ${theme.shadows[1]};
            transition-timing-function: ${theme.transitions.easing.easeIn};
            transition-duration: ${theme.transitions.duration.standard};
            transition-property: box-shadow;
          }
          &:checked + * {
            background-color: ${theme.palette.intentions[color].main};
          }
          &:not(:disabled):hover + * {
            box-shadow: ${theme.shadows[3]};
          }
        `}
        type="checkbox"
        onClick={onClick}
        {...props}
      />
      <div
        css={css({
          width: 24,
          height: 24,
          fontSize: 18,
          color: theme.palette.intentions[color].contrastText,
          backgroundColor: theme.palette.intentions[color].contrastText,
          borderColor: theme.palette.intentions[color].main,
          borderWidth: 1,
          borderStyle: "solid",
          borderRadius: 5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
        })}
        onAnimationEnd={onAnimationEnd}
      >
        <Icon.Check />
      </div>
      {hasChildren ? (
        <span
          css={css({
            marginLeft: "0.5em",
          })}
        >
          {children}
        </span>
      ) : null}
    </RootComponent>
  );
};
